<template>
  <div>
    <simpletoolbar></simpletoolbar>
    <div class="max-w-4xl mx-auto px-4 py-8">
      <h1 class="text-3xl font-bold mb-8 text-stone-800 dark:text-stone-100">Terms of Service</h1>

      <!-- Introduction Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Welcome to Spin the Names</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          Spin the Names is developed and maintained by an independent developer. These Terms of Service ("Terms")
          govern your access to and use of the Spin the Names website and application (the "Service"). Please read these
          Terms carefully before using the Service.
        </p>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of the
          Terms,
          then you may not access the Service.
        </p>
      </section>

      <!-- Definitions Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Definitions</h2>
        <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
          <li><strong>Service</strong> refers to the Spin the Names website and application operated by Spin the Names.
          </li>
          <li><strong>Personal Data</strong> refers to data about a living individual who can be identified from those
            data.</li>
          <li><strong>Usage Data</strong> refers to data collected automatically that is generated by the use of the
            Service.</li>
          <li><strong>Cookies</strong> are small files stored on your device.</li>
          <li><strong>Account</strong> means a unique account created for you to access our Service or parts of our
            Service.</li>
          <li><strong>Content</strong> refers to the information, text, graphics, photos or other materials uploaded,
            downloaded or appearing on the Service.</li>
        </ul>
      </section>

      <!-- Account Creation and Responsibilities Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Account Creation and Responsibilities
        </h2>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Account Creation</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            When you create an account with Spin the Names, you guarantee that the information you provide is accurate,
            complete, and current at all times.
            Inaccurate, incomplete, or obsolete information may result in the immediate termination of your account on
            the Service.
          </p>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Account Security</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            You are responsible for maintaining the confidentiality of your account and password, including but not
            limited to
            restricting access to your computer and/or account. You agree to accept responsibility for any and all
            activities
            or actions that occur under your account and/or password.
          </p>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            You must notify Spin the Names immediately upon becoming aware of any breach of security or unauthorized use
            of your account.
          </p>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Account Restrictions</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            Spin the Names reserves the right to refuse service, terminate accounts, remove or edit content, or cancel
            orders at the sole discretion of its developer.
          </p>
        </div>
      </section>

      <!-- Content and Conduct Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Content and Conduct</h2>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">User Content</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            The Service allows you to create and share wheel configurations that may include text, names, and other
            content.
            You are responsible for the content you create and share using the Service.
          </p>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            By creating and sharing content through our Service, you represent and warrant that:
          </p>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>The creation and sharing of your content doesn't violate the privacy rights, publicity rights,
              copyrights, contract rights, or any other rights of any person or entity.</li>
            <li>You have the legal right and authority to create and share all elements of your content.</li>
            <li>Your content is not unlawful, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or
              otherwise objectionable.</li>
          </ul>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Prohibited Activities</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            You may not use the Service for any purpose that is illegal or prohibited by these Terms. You agree not to
            use the Service:
          </p>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>In any way that violates any applicable national or international law or regulation.</li>
            <li>To transmit, or procure the sending of, any advertising or promotional material, including any "junk
              mail", "chain letter," "spam," or any other similar solicitation.</li>
            <li>To impersonate or attempt to impersonate the developer, another user, or any other person or entity.
            </li>
            <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service, or
              which may harm Spin the Names or users of the Service or expose them to liability.</li>
            <li>To use the Service in any manner that could disable, overburden, damage, or impair the site or interfere
              with any other party's use of the Service.</li>
            <li>To use any robot, spider, or other automatic device, process, or means to access the Service for any
              purpose, including monitoring or copying any of the material on the Service.</li>
          </ul>
        </div>
      </section>

      <!-- Intellectual Property Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Intellectual Property</h2>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Service Content</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            The Service and its original content (excluding content provided by users), features, and functionality are
            and will remain
            the exclusive property of Spin the Names and its licensors. The Service is protected by copyright,
            trademark, and other
            laws of both the United States and foreign countries.
          </p>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            The trademarks and trade dress of Spin the Names may not be used in connection with any product or service
            without prior written consent from Spin the Names.
          </p>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">User Content License</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            By posting content to the Service, you grant Spin the Names a non-exclusive, royalty-free, transferable,
            sublicensable, worldwide license
            to use, display, reproduce, and distribute your content in connection with the operation of the Service.
          </p>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            You can delete your content at any time by deleting your account or the specific wheel configurations.
            However, you
            understand that if you choose to share a wheel configuration publicly, other users may have accessed or
            copied it,
            and Spin the Names cannot control what they do with that information.
          </p>
        </div>
      </section>

      <!-- Disclaimer of Warranties Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Disclaimer of Warranties</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis.
          The Service is provided without warranties of any kind, whether express or implied, including, but not limited
          to,
          implied warranties of merchantability, fitness for a particular purpose, non-infringement, or course of
          performance.
        </p>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          Spin the Names does not warrant that:
        </p>
        <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
          <li>The Service will function uninterrupted, secure, or available at any particular time or location;</li>
          <li>Any errors or defects will be corrected;</li>
          <li>The Service is free of viruses or other harmful components;</li>
          <li>The results of using the Service will meet your requirements.</li>
        </ul>
      </section>

      <!-- Limitation of Liability Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Limitation of Liability</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          In no event shall Spin the Names, nor any partners, agents, suppliers, or affiliates of Spin the Names, be
          liable for any
          indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of
          profits, data,
          use, goodwill, or other intangible losses, resulting from:
        </p>
        <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
          <li>Your access to or use of or inability to access or use the Service;</li>
          <li>Any conduct or content of any third party on the Service;</li>
          <li>Any content obtained from the Service; and</li>
          <li>Unauthorized access, use, or alteration of your transmissions or content;</li>
        </ul>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          Whether based on warranty, contract, tort (including negligence), or any other legal theory, whether or not we
          have been informed
          of the possibility of such damage, and even if a remedy set forth herein is found to have failed of its
          essential purpose.
        </p>
      </section>

      <!-- Indemnification Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Indemnification</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          You agree to defend, indemnify, and hold harmless Spin the Names and its licensees and licensors from
          and against any and all claims,
          damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to
          attorney's fees),
          resulting from or arising out of:
        </p>
        <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
          <li>Your use and access of the Service, by you or any person using your account and password;</li>
          <li>A breach of these Terms; or</li>
          <li>Content posted on the Service.</li>
        </ul>
      </section>

      <!-- Governing Law and Dispute Resolution Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Governing Law and Dispute Resolution
        </h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          These Terms shall be governed and construed in accordance with the laws of the United States,
          without regard to its conflict of law provisions.
        </p>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
          If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of
          these Terms will remain in effect.
        </p>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          Any disputes arising out of or relating to these Terms or the Service will first be resolved through informal
          negotiation.
          If the dispute cannot be resolved through negotiation, it shall be submitted to binding arbitration in
          accordance with
          the rules of the American Arbitration Association.
        </p>
      </section>

      <!-- Changes to Terms Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Changes to Terms</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          Spin the Names reserves the right to modify or replace these Terms at any time. If a revision is material,
          notice will be provided at least 30 days prior to any new terms taking effect. What constitutes a material
          change
          will be determined at the sole discretion of Spin the Names.
        </p>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          By continuing to access or use our Service after those revisions become effective, you agree to be bound by
          the revised terms.
          If you do not agree to the new terms, please stop using the Service.
        </p>
      </section>

      <!-- Contact Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Contact Information</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          If you have any questions about these Terms, please contact:
        </p>
        <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
          <li>By email: ash@spinthenames.com</li>
        </ul>
      </section>

      <!-- Back to Top link -->
      <div class="text-center mb-8">
        <a href="#" @click.prevent="scrollToTop"
          class="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300">
          <i class="fas fa-arrow-up mr-2"></i> Back to Top
        </a>
      </div>

      <div
        class="text-center text-sm text-stone-500 dark:text-stone-400 mt-12 pt-6 border-t border-stone-200 dark:border-stone-700">
        <p>Last Updated: June 5, 2025</p>
      </div>
    </div>
    <app-footer />
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { useHead } from '@vueuse/head';
import simpletoolbar from '@components/ui/simpletoolbar.vue';
import appFooter from '@components/ui/appFooter.vue';

// Page head metadata
useHead({
  title: 'Terms of Service - Spin the Names',
  meta: [
    { name: 'description', content: 'Read the Terms of Service for Spin the Names. These terms outline the rules, guidelines, and legal agreement between users and the random wheel selection tool.' },
    { property: 'og:title', content: 'Terms of Service - Spin the Names' },
    { property: 'og:description', content: 'Read the Terms of Service for Spin the Names.' },
    { name: 'twitter:title', content: 'Terms of Service - Spin the Names' },
    { name: 'twitter:description', content: 'Read the Terms of Service for Spin the Names.' }
  ]
});

// Scroll to top on mount
onMounted(() => {
  window.scrollTo(0, 0);
});

// Smooth scroll handler for Back to Top link
function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}
</script>
